import {
    API_BASE, ERROR_CODES,
    type ChallengeResponse, type ChildDetail, type ChildInputDraft as ChildInput, type ChildMilestone, type MilestoneInput, type ChildStatus, type ChildSummary, type ErrorCode, type Me,
    type OrganizationOverview, type OrganizationRef, type SignupInput,
    type ChildMessage, type ImageConsentInput, type MessageSituation, type ReplyInput,
    type Conversation, type ConversationItem, type PushConfig, type PushSubscriptionInput,
    type TranscriptionResult, type TranscriptionSource,
    type MessageList, type MessageLocale, type MessageLocalePreference, type TranslateResult,
    type ChangePasswordInput, type NotificationPreferences, type NotificationPreferencesInput,
} from '@/contract'

/* Spec PARTNER-001 — cliente HTTP da API /api/v1/partner.
   Envelope { data } no sucesso e { error: { code, ...extra } } na falha; 204 vira undefined. */

export type MilestoneList = { items: ChildMilestone[]; mediaEnabled: boolean }

export type ApiErrorCode = ErrorCode | 'network' | 'unknown'

export class PartnerApiError extends Error {
    constructor(
        readonly status: number,
        readonly code: ApiErrorCode,
        readonly extra: Record<string, unknown> = {},
    ) {
        super(code)
        this.name = 'PartnerApiError'
    }

    /* Bloqueio temporario (PARTNER_ACCOUNT_LOCKED, PARTNER_RATE_LIMITED) */
    get retryAt(): string | null {
        return typeof this.extra.retryAt === 'string' ? this.extra.retryAt : null
    }

    get attemptsLeft(): number | null {
        return typeof this.extra.attemptsLeft === 'number' ? this.extra.attemptsLeft : null
    }

    /* PARTNER_EMAIL_NOT_VERIFIED: desafio novo para confirmar o e-mail */
    get challengeId(): string | null {
        return typeof this.extra.challengeId === 'string' ? this.extra.challengeId : null
    }

    get destinationMasked(): string | null {
        return typeof this.extra.destinationMasked === 'string' ? this.extra.destinationMasked : null
    }

    /* PARTNER_VALIDATION: { campo: motivo } */
    get fields(): Record<string, string> {
        const f = this.extra.fields
        if (!f || typeof f !== 'object') return {}
        return Object.fromEntries(Object.entries(f as Record<string, unknown>).map(([k, v]) => [k, String(v)]))
    }
}

function toApiError(status: number, body: unknown): PartnerApiError {
    const error = body && typeof body === 'object' ? (body as { error?: unknown }).error : null
    if (error && typeof error === 'object') {
        const { code, ...extra } = error as { code?: unknown } & Record<string, unknown>
        if (typeof code === 'string' && (ERROR_CODES as readonly string[]).includes(code)) {
            return new PartnerApiError(status, code as ErrorCode, extra)
        }
    }
    if (status === 401) return new PartnerApiError(status, 'PARTNER_SESSION_REQUIRED')
    if (status === 404) return new PartnerApiError(status, 'PARTNER_NOT_FOUND')
    if (status === 429) return new PartnerApiError(status, 'PARTNER_RATE_LIMITED')
    return new PartnerApiError(status, 'unknown')
}

export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    let res: Response
    try {
        res = await fetch(`${API_BASE}${path}`, {
            method,
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                // FormData (anexos, PWA-011): o navegador define o Content-Type com o boundary
                ...(body !== undefined && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
            },
            body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
        })
    } catch {
        throw new PartnerApiError(0, 'network')
    }
    if (res.status === 204) return undefined as T
    let json: unknown = null
    const text = await res.text()
    if (text) {
        try {
            json = JSON.parse(text)
        } catch {
            json = null
        }
    }
    if (!res.ok) throw toApiError(res.status, json)
    if (!json || typeof json !== 'object' || !('data' in json)) throw new PartnerApiError(res.status, 'unknown')
    return (json as { data: T }).data
}

const enc = encodeURIComponent

function query(params: Record<string, string | undefined>): string {
    const entries = Object.entries(params).filter((e): e is [string, string] => !!e[1])
    return entries.length ? `?${new URLSearchParams(entries).toString()}` : ''
}

export const partnerApi = {
    // Publico
    organizationByCode: (code: string) => request<OrganizationRef>('GET', `/organizations/${enc(code)}`),
    signup: (input: SignupInput) => request<ChallengeResponse>('POST', '/auth/signup', input),
    signupVerify: (challengeId: string, code: string) =>
        request<{ status: 'pending_approval' }>('POST', '/auth/signup/verify', { challengeId, code }),
    // PARTNER-005 D-06: idioma da tela (o e-mail com o codigo sai nele)
    signupResend: (challengeId: string, locale?: MessageLocale) => request<ChallengeResponse>('POST', '/auth/signup/resend', { challengeId, locale }),
    login: (input: { email: string; password: string; remember: boolean }) => request<{ me: Me }>('POST', '/auth/login', input),
    forgot: (email: string) => request<{ challengeId: string }>('POST', '/auth/forgot', { email }),
    forgotReset: (input: { challengeId: string; code: string; password: string }) =>
        request<{ ok: true }>('POST', '/auth/forgot/reset', input),
    logout: () => request<void>('POST', '/auth/logout'),

    // Com sessao
    me: () => request<Me>('GET', '/me'),
    // Spec PWA-020 / PARTNER-005: idioma da pessoa (interface e traducao das mensagens dos padrinhos)
    messageLocale: () => request<MessageLocalePreference>('GET', '/me/message-locale'),
    setMessageLocale: (locale: MessageLocale) => request<MessageLocalePreference>('PATCH', '/me/message-locale', { locale }),
    // Spec PARTNER-004: Configuracoes (avisos por tipo, trocar senha, sair de todos os aparelhos)
    notifications: () => request<NotificationPreferences>('GET', '/me/notifications'),
    setNotifications: (input: NotificationPreferencesInput) => request<NotificationPreferences>('PATCH', '/me/notifications', input),
    changePassword: (input: ChangePasswordInput) => request<{ ok: true }>('POST', '/me/password', input),
    logoutAll: () => request<void>('POST', '/auth/logout-all'),
    translateMessage: (id: string) => request<TranslateResult>('POST', `/messages/${enc(id)}/translation`),
    translateTranscript: (attachmentId: string) => request<TranslateResult>('POST', `/attachments/sponsor/${enc(attachmentId)}/translation`),
    organization: () => request<OrganizationOverview>('GET', '/organization'),
    children: (filters: { q?: string; status?: ChildStatus }) =>
        request<{ items: ChildSummary[] }>('GET', `/children${query({ q: filters.q, status: filters.status })}`),
    child: (id: string) => request<ChildDetail>('GET', `/children/${enc(id)}`),
    createChild: (input: ChildInput) => request<ChildDetail>('POST', '/children', input),
    updateChild: (id: string, input: ChildInput) => request<ChildDetail>('PATCH', `/children/${enc(id)}`, input),
    submitChild: (id: string) => request<ChildDetail>('POST', `/children/${enc(id)}/submit`),
    deleteChild: (id: string) => request<void>('DELETE', `/children/${enc(id)}`),
    // Spec PWA-009: mensagens dos padrinhos para as criancas
    messages: (filters: { situation?: MessageSituation }) =>
        request<MessageList>('GET', `/messages${query({ situacao: filters.situation })}`),
    deliverMessage: (id: string) => request<ChildMessage>('POST', `/messages/${enc(id)}/deliver`),
    createReply: (id: string, input: ReplyInput) => request<ChildMessage>('POST', `/messages/${enc(id)}/reply`, input),
    updateReply: (id: string, input: ReplyInput) => request<ChildMessage>('PATCH', `/messages/${enc(id)}/reply`, input),
    // Spec PWA-011: fotos e audio da resposta; consentimento de imagem da crianca
    addAttachment: (id: string, file: Blob, kind: 'photo' | 'audio', showsChild: boolean) => {
        const form = new FormData()
        form.append('file', file, kind === 'photo' ? 'foto.jpg' : 'audio')
        form.append('kind', kind)
        form.append('showsChild', String(showsChild))
        return request<ChildMessage>('POST', `/messages/${enc(id)}/reply/attachments`, form)
    },
    removeAttachment: (id: string, attachmentId: string) =>
        request<ChildMessage>('DELETE', `/messages/${enc(id)}/reply/attachments/${enc(attachmentId)}`),
    setImageConsent: (childId: string, input: ImageConsentInput) => request<ChildDetail>('PUT', `/children/${enc(childId)}/image-consent`, input),
    // Spec PWA-012: a OSP escreve primeiro ou retoma a conversa com um padrinho
    // Spec PWA-018: botao "Transcrever" (audio da crianca: reply; audio do padrinho: sponsor)
    transcribe: (source: TranscriptionSource, attachmentId: string) =>
        request<TranscriptionResult>('POST', `/attachments/${enc(source)}/${enc(attachmentId)}/transcription`),
    conversation: (childId: string, sponsorId: string) =>
        request<Conversation>('GET', `/children/${enc(childId)}/conversations/${enc(sponsorId)}`),
    startMessage: (childId: string, sponsorId: string, input: ReplyInput) =>
        request<Conversation>('POST', `/children/${enc(childId)}/conversations/${enc(sponsorId)}/messages`, input),
    addChildMessageAttachment: (id: string, file: Blob, kind: 'photo' | 'audio', showsChild: boolean) => {
        const form = new FormData()
        form.append('file', file, kind === 'photo' ? 'foto.jpg' : 'audio')
        form.append('kind', kind)
        form.append('showsChild', String(showsChild))
        return request<ConversationItem>('POST', `/child-messages/${enc(id)}/attachments`, form)
    },
    removeChildMessageAttachment: (id: string, attachmentId: string) =>
        request<ConversationItem>('DELETE', `/child-messages/${enc(id)}/attachments/${enc(attachmentId)}`),
    // Spec PARTNER-002: avisos deste aparelho
    pushConfig: () => request<PushConfig>('GET', '/push/config'),
    pushSubscribe: (input: PushSubscriptionInput) => request<void>('PUT', '/push/subscriptions', input),
    pushUnsubscribe: (endpoint: string) => request<void>('DELETE', '/push/subscriptions', { endpoint }),
    pushTest: (endpoint: string) => request<{ sent: true }>('POST', '/push/test', { endpoint }),
    // Spec PARTNER-003: foto de perfil e marcos na vida da crianca
    setChildPhoto: (childId: string, photo: Blob) => {
        const form = new FormData()
        form.append('file', photo, 'foto.jpg')
        return request<ChildDetail>('PUT', `/children/${enc(childId)}/photo`, form)
    },
    removeChildPhoto: (childId: string) => request<ChildDetail>('DELETE', `/children/${enc(childId)}/photo`),
    milestones: (childId: string) => request<MilestoneList>('GET', `/children/${enc(childId)}/milestones`),
    saveMilestone: (childId: string, milestoneId: string | null, input: MilestoneInput, photo: Blob | null, removePhoto = false) => {
        const form = new FormData()
        for (const [key, value] of Object.entries(input)) form.append(key, value)
        if (photo) form.append('file', photo, 'foto.jpg')
        if (removePhoto) form.append('removePhoto', 'true')
        return milestoneId
            ? request<MilestoneList>('PATCH', `/children/${enc(childId)}/milestones/${enc(milestoneId)}`, form)
            : request<MilestoneList>('POST', `/children/${enc(childId)}/milestones`, form)
    },
    deleteMilestone: (childId: string, milestoneId: string) =>
        request<MilestoneList>('DELETE', `/children/${enc(childId)}/milestones/${enc(milestoneId)}`),
}
